// src/prototype/components/EAHimssAssessmentsCard.tsx
// ============================================================
// HIMSS ASSESSMENTS (D6, D7) — optional intake card on the Documents page (E1, Oct 21).
//
// Optional by design, with "We don't hold any" as a first-class answer, because most
// clients hold one or two of these at most and an empty form must not read as a gap.
//
// Context only at GA: values are shown back for the client to confirm. Not a scoring input,
// no R(c) wiring. Uploads go through the same extraction path as D1–D4 (onUploadFile).
//
// Copy rule: model names are factual labels for the client's own results. No logo, no
// wording that implies a relationship with HIMSS.
// ============================================================

import { useState } from 'react';
import { ChevronDown, ChevronUp, Paperclip, CheckCircle2 } from 'lucide-react';
import {
  MATURITY_MODELS, DHI_DIMENSIONS, DHI_OVERALL_MAX,
  validateModel, validateDhi, answeredCount, heldCount, readBackLines,
  type MaturityModelId, type MaturityModelResult, type DhiResult, type ScopeType,
} from '../data/himss/himssAssessments';
import { useHimssAssessments } from '../hooks/useHimssAssessments';

interface Props {
  /** Same extraction path as the D-instrument uploads. */
  onUploadFile?: (file: File) => void;
  /** Who is entering the values (entered-by). */
  enteredBy?: string;
}

const inputCls = 'text-xs text-[#18365E] border border-[#e5e7eb] rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#18365E]/20';
const labelCls = 'text-[10px] font-medium text-[#717182] uppercase tracking-wide';

function YesNo({ value, onChange, yes = 'Yes', no = 'No', name }: {
  value: boolean | null; onChange: (v: boolean) => void; yes?: string; no?: string; name: string;
}) {
  return (
    <div className="inline-flex rounded-lg border border-[#e5e7eb] bg-white p-0.5" role="radiogroup" aria-label={name}>
      {[true, false].map((v) => (
        <button
          key={String(v)}
          type="button"
          role="radio"
          aria-checked={value === v}
          onClick={() => onChange(v)}
          className={`px-2.5 py-0.5 rounded-md text-[11px] font-medium ${value === v ? 'bg-[#18365E] text-white' : 'text-[#717182] hover:text-[#18365E]'}`}
        >
          {v ? yes : no}
        </button>
      ))}
    </div>
  );
}

function FieldError({ msg }: { msg?: string }) {
  return msg ? <p className="text-[10.5px] text-red-600 mt-0.5">{msg}</p> : null;
}

function ScopeFields({ scope, facilities, onScope, onFacilities, error }: {
  scope: ScopeType; facilities: string; onScope: (s: ScopeType) => void; onFacilities: (f: string) => void; error?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className={labelCls}>Facilities in scope</span>
      <select value={scope} onChange={(e) => onScope(e.target.value as ScopeType)} className={inputCls} aria-label="Facilities in scope">
        <option value="whole_system">Whole system</option>
        <option value="listed_facilities">Listed facilities</option>
      </select>
      {scope === 'listed_facilities' && (
        <input value={facilities} onChange={(e) => onFacilities(e.target.value)} placeholder="Facility names, comma separated" className={inputCls} aria-label="Facilities" />
      )}
      <FieldError msg={error} />
    </div>
  );
}

function FileField({ fileName, onFile, label }: { fileName: string | null; onFile: (f: File) => void; label: string }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] font-medium text-[#18365E] cursor-pointer hover:text-[#FF6E4C]">
      <Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
      {fileName ? <span className="truncate max-w-[200px]">{fileName}</span> : label}
      <input type="file" className="hidden" aria-label={label} onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </label>
  );
}

export function EAHimssAssessmentsCard({ onUploadFile, enteredBy = 'Client user' }: Props) {
  const { himss, update } = useHimssAssessments();
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const stamp = () => ({ enteredBy, enteredAt: new Date().toISOString() });

  const setModel = (id: MaturityModelId, patch: Partial<MaturityModelResult>) =>
    update((prev) => ({ ...prev, noneHeld: false, models: { ...prev.models, [id]: { ...prev.models[id], ...patch, ...stamp() } } }));
  const setDhi = (patch: Partial<DhiResult>) =>
    update((prev) => ({ ...prev, noneHeld: false, dhi: { ...prev.dhi, ...patch, ...stamp() } }));
  const setNoneHeld = () =>
    update((prev) => ({
      noneHeld: true,
      models: Object.fromEntries(Object.entries(prev.models).map(([k, m]) => [k, { ...m, held: false, ...stamp() }])) as typeof prev.models,
      dhi: { ...prev.dhi, completed: false, ...stamp() },
    }));

  const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));
  const answered = answeredCount(himss);
  const held = heldCount(himss);
  const total = MATURITY_MODELS.length + 1;
  const lines = readBackLines(himss);
  const dhiErr = validateDhi(himss.dhi);

  const status = himss.noneHeld
    ? 'None held'
    : answered === 0 ? 'Optional' : `${held} held · ${answered} of ${total} answered`;

  return (
    <div className="bg-white border border-[#e5e7eb] rounded-xl">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-3 px-4 py-3.5 text-left"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold tracking-widest text-[#717182]">D6 · D7</span>
            <h3 className="text-sm font-semibold text-[#18365E]">HIMSS assessments</h3>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${answered > 0 ? 'bg-[#EEF2F7] text-[#18365E]' : 'bg-[#F5F7FA] text-[#717182]'}`}>{status}</span>
          </div>
          <p className="text-xs leading-relaxed text-[#717182] mt-1">
            Maturity model results (EMRAM, O-EMRAM, INFRAM, AMAM) and a Digital Health Indicator
            report, if you hold them. Recorded as context alongside your self-ratings; they do not
            change any score.
          </p>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-[#717182] shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-[#717182] shrink-0 mt-1" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-[#e5e7eb]">
          <div className="flex items-center justify-between gap-3 py-3">
            <p className="text-[11px] text-[#717182]">Share only what your HIMSS terms permit.</p>
            <button
              type="button"
              onClick={setNoneHeld}
              className={`px-3 py-1 rounded-lg text-[11px] font-semibold border ${himss.noneHeld ? 'bg-[#18365E] text-white border-[#18365E]' : 'text-[#18365E] border-[#18365E]/30 hover:bg-[#F5F7FA]'}`}
            >
              We don't hold any of these
            </button>
          </div>

          {/* D6: maturity models */}
          <h4 className="text-[10px] font-bold tracking-widest text-[#717182] mb-2">MATURITY MODEL RESULTS</h4>
          <div className="flex flex-col divide-y divide-[#e5e7eb] border border-[#e5e7eb] rounded-lg mb-4">
            {MATURITY_MODELS.map((m) => {
              const r = himss.models[m.id];
              const err = validateModel(r);
              return (
                <div key={m.id} className="px-3 py-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <span className="text-xs font-semibold text-[#18365E]">{m.name}</span>
                      <span className="text-[11px] text-[#717182] ml-1.5">{m.longName}</span>
                    </div>
                    <YesNo name={`${m.name} held`} value={r.held} onChange={(v) => setModel(m.id, { held: v })} yes="Held" no="Not held" />
                  </div>
                  {r.held === true && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2.5">
                      <div className="flex flex-col gap-1">
                        <span className={labelCls}>Stage achieved</span>
                        <select
                          value={r.stage ?? ''}
                          onChange={(e) => setModel(m.id, { stage: e.target.value === '' ? null : Number(e.target.value) })}
                          className={inputCls}
                          aria-label={`${m.name} stage`}
                        >
                          <option value="">Select</option>
                          {[0, 1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>Stage {n}</option>)}
                        </select>
                        <FieldError msg={err.stage} />
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={labelCls}>Validation</span>
                        <select
                          value={r.validation ?? ''}
                          onChange={(e) => setModel(m.id, { validation: (e.target.value || null) as MaturityModelResult['validation'] })}
                          className={inputCls}
                          aria-label={`${m.name} validation`}
                        >
                          <option value="">Select</option>
                          <option value="himss_validated">Validated by HIMSS</option>
                          <option value="self_assessed">Self-assessed</option>
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <span className={labelCls}>Assessment date</span>
                        <input type="date" max={today} value={r.assessmentDate ?? ''} onChange={(e) => setModel(m.id, { assessmentDate: e.target.value || null })} className={inputCls} aria-label={`${m.name} assessment date`} />
                        <FieldError msg={err.assessmentDate} />
                      </div>
                      <ScopeFields
                        scope={r.scope} facilities={r.facilities}
                        onScope={(scope) => setModel(m.id, { scope })}
                        onFacilities={(facilities) => setModel(m.id, { facilities })}
                        error={err.facilities}
                      />
                      <div className="col-span-2 md:col-span-4">
                        <FileField
                          label="Attach scorecard or validation letter (optional)"
                          fileName={r.fileName}
                          onFile={(f) => { setModel(m.id, { fileName: f.name }); onUploadFile?.(f); }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* D7: Digital Health Indicator */}
          <h4 className="text-[10px] font-bold tracking-widest text-[#717182] mb-2">DIGITAL HEALTH INDICATOR</h4>
          <div className="border border-[#e5e7eb] rounded-lg px-3 py-2.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-[#18365E]">Digital Health Indicator report</span>
              <YesNo name="Digital Health Indicator completed" value={himss.dhi.completed} onChange={(v) => setDhi({ completed: v })} yes="Completed" no="Not completed" />
            </div>
            {himss.dhi.completed === true && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2.5">
                <div className="flex flex-col gap-1">
                  <span className={labelCls}>Overall score (0 to {DHI_OVERALL_MAX})</span>
                  <input type="number" min={0} max={DHI_OVERALL_MAX} value={himss.dhi.overall ?? ''} onChange={(e) => setDhi({ overall: num(e.target.value) })} className={inputCls} aria-label="Overall score" />
                  <FieldError msg={dhiErr.overall} />
                </div>
                {DHI_DIMENSIONS.map((d) => (
                  <div key={d.id} className="flex flex-col gap-1">
                    <span className={labelCls}>{d.label}</span>
                    <input
                      type="number" min={0}
                      value={himss.dhi.dimensions[d.id] ?? ''}
                      onChange={(e) => setDhi({ dimensions: { ...himss.dhi.dimensions, [d.id]: num(e.target.value) } })}
                      className={inputCls}
                      aria-label={`${d.label} score`}
                    />
                    <FieldError msg={dhiErr[d.id]} />
                  </div>
                ))}
                <div className="flex flex-col gap-1">
                  <span className={labelCls}>Assessment date</span>
                  <input type="date" max={today} value={himss.dhi.assessmentDate ?? ''} onChange={(e) => setDhi({ assessmentDate: e.target.value || null })} className={inputCls} aria-label="Digital Health Indicator assessment date" />
                  <FieldError msg={dhiErr.assessmentDate} />
                </div>
                <ScopeFields
                  scope={himss.dhi.scope} facilities={himss.dhi.facilities}
                  onScope={(scope) => setDhi({ scope })}
                  onFacilities={(facilities) => setDhi({ facilities })}
                  error={dhiErr.facilities}
                />
                <div className="col-span-2 md:col-span-4">
                  <FileField
                    label="Attach report (optional)"
                    fileName={himss.dhi.fileName}
                    onFile={(f) => { setDhi({ fileName: f.name }); onUploadFile?.(f); }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Read-back for client validation */}
          {lines.length > 0 && (
            <div className="mt-4 rounded-lg bg-[#F5F7FA] px-3 py-2.5">
              <h4 className="text-[10px] font-bold tracking-widest text-[#717182] mb-1.5">PLEASE CONFIRM WHAT WE RECORDED</h4>
              <ul className="flex flex-col gap-1">
                {lines.map((l) => (
                  <li key={l} className="flex items-start gap-1.5 text-xs text-[#18365E]">
                    <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981] mt-0.5 shrink-0" aria-hidden="true" />
                    {l}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default EAHimssAssessmentsCard;
